import { API_ORIGIN } from "@/lib/apiOrigin";
// Phase 44.03 — Public tracking landing premium (/track/{token})
// Obiettivo: il destinatario WhatsApp vive l'esperienza DapNow live senza
// login, con UX moderna stile food-delivery e una CTA virale "Spedisci con
// DapNow". Niente dati sensibili.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, AlertCircle, XCircle, Truck, Wifi, WifiOff, Navigation,
  Sparkles, Clock, PackageCheck,
  Bike, Car, Package, Send,
} from "lucide-react";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";
import OrderTrackingMap from "@/components/OrderTrackingMap";
import Logo from "@/components/Logo";
import { trackPublicEvent } from "@/lib/analytics";

const API = `${API_ORIGIN}/api`;
const POLL_MS = 15_000;
const NEAR_THRESHOLD_MIN = 5;

// Cromia semantica unificata col resto dell'app:
//  ARANCIONE = verso ritiro
//  BLU       = in consegna (pacco già ritirato)
//  BLU intenso = driver quasi arrivato
//  VERDE     = consegnato
const HERO_BY_STATUS = {
  pending: {
    accent: "#71717A",
    bg: "from-zinc-100 via-white to-white",
    border: "border-zinc-300",
    title: "In attesa di un driver",
    sub: "Stiamo assegnando il trasportatore migliore.",
    pulse: false,
  },
  accepted: {
    accent: "#F59E0B",
    bg: "from-amber-100 via-white to-white",
    border: "border-amber-300",
    title: "Il driver sta andando al ritiro del pacco",
    sub: "Il pacco sarà ritirato a breve.",
    pulse: true,
  },
  picking_up: {
    accent: "#F59E0B",
    bg: "from-amber-100 via-white to-white",
    border: "border-amber-300",
    title: "Il driver è al punto di ritiro",
    sub: "Stiamo prelevando il pacco.",
    pulse: true,
  },
  picked_up: {
    accent: "#0052FF",
    bg: "from-blue-100 via-white to-white",
    border: "border-blue-300",
    title: "Il tuo pacco è in arrivo",
    sub: "Il driver è partito con il tuo pacco.",
    pulse: true,
  },
  delivering: {
    accent: "#0052FF",
    bg: "from-blue-100 via-white to-white",
    border: "border-blue-300",
    title: "Il tuo pacco è in arrivo",
    sub: "Segui il driver in tempo reale.",
    pulse: true,
  },
  delivered: {
    accent: "#16A34A",
    bg: "from-emerald-100 via-white to-white",
    border: "border-emerald-300",
    title: "Pacco consegnato",
    sub: "La consegna è stata completata con successo.",
    pulse: false,
  },
  cancelled: {
    accent: "#DC2626",
    bg: "from-red-50 via-white to-white",
    border: "border-red-300",
    title: "Consegna annullata",
    sub: "Questo ordine è stato annullato.",
    pulse: false,
  },
};

// Variante "near": delivering + ETA <= 5 min → blu intenso, copy speciale.
const NEAR_HERO = {
  accent: "#0033CC",
  bg: "from-blue-200 via-blue-50 to-white",
  border: "border-blue-400",
  title: "Il driver è quasi arrivato",
  sub: "Preparati a ricevere il pacco.",
  pulse: true,
};

const VEHICLE_META = {
  scooter: { Icon: Bike, label: "Scooter" },
  auto:    { Icon: Car,  label: "Auto" },
  furgone: { Icon: Truck, label: "Furgone" },
};

function fmtUpdated(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diff = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
    if (diff < 60) return `${diff}s fa`;
    if (diff < 3600) return `${Math.round(diff/60)} min fa`;
    return d.toLocaleTimeString("it-CH", { hour: "2-digit", minute: "2-digit" });
  } catch { return null; }
}

function ETACard({ etaMin, distanceKm, lastLocAt, accent, isDelivered }) {
  if (isDelivered) {
    return (
      <div className="rounded-2xl border border-emerald-300 bg-emerald-50/70 p-4 sm:p-5 flex items-center gap-3">
        <PackageCheck className="h-7 w-7 text-emerald-700 shrink-0" />
        <div>
          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Consegna completata</p>
          <p className="text-sm text-dapnow-ink mt-0.5">Il pacco è stato consegnato.</p>
        </div>
      </div>
    );
  }
  if (etaMin === null || etaMin === undefined) return null;
  const eta = Math.max(1, Math.round(etaMin));
  const km = distanceKm !== null && distanceKm !== undefined ? Number(distanceKm).toFixed(1) : null;
  const updated = fmtUpdated(lastLocAt);
  return (
    <div
      className="rounded-2xl border bg-white p-4 sm:p-5"
      style={{ borderColor: accent + "44" }}
      data-testid="public-eta-card"
    >
      <div className="flex items-end gap-5 flex-wrap">
        <div>
          <p className="overline text-[10px] font-bold text-dapnow-muted">ETA stimato</p>
          <p className="font-display font-extrabold tracking-tight leading-none mt-1" style={{ color: accent, fontSize: "44px" }}>
            {eta}<span className="text-lg ml-1 text-dapnow-muted">min</span>
          </p>
        </div>
        {km && (
          <div>
            <p className="overline text-[10px] font-bold text-dapnow-muted">Distanza</p>
            <p className="font-display text-2xl font-extrabold tracking-tight mt-1">{km}<span className="text-sm text-dapnow-muted ml-1">km</span></p>
          </div>
        )}
        <div className="ml-auto text-right">
          <span className="inline-flex items-center gap-1 text-xs text-dapnow-muted">
            <Clock className="h-3 w-3" />
            {updated ? `Aggiornato ${updated}` : "In aggiornamento…"}
          </span>
        </div>
      </div>
    </div>
  );
}

function DriverCard({ driver, accent }) {
  if (!driver?.name) return null;
  const vm = VEHICLE_META[driver.vehicle_type] || { Icon: Truck, label: driver.vehicle_type || "Veicolo" };
  const VIcon = vm.Icon;
  // Phase 44.29 — Composizione visiva veicolo per il destinatario:
  //   "Fiat Panda bianca" o solo "Fiat Panda" se manca il colore
  const vehicleParts = [driver.brand, driver.model].filter(Boolean).join(" ").trim();
  const vehicleVisual = driver.color
    ? `${vehicleParts} ${String(driver.color).toLowerCase()}`.trim()
    : vehicleParts;
  return (
    <div className="rounded-2xl border border-dapnow-line bg-white p-4 sm:p-5 flex items-center gap-4" data-testid="public-driver-card">
      <div
        className="h-14 w-14 rounded-full grid place-items-center text-white font-display font-extrabold text-xl shrink-0"
        style={{ background: accent }}
      >
        {driver.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "D"}
      </div>
      <div className="flex-1 min-w-0">
        <p className="overline text-[10px] font-bold text-dapnow-muted">Il tuo trasportatore</p>
        <p className="font-display text-lg font-bold tracking-tight truncate">{driver.name}</p>
        {/* Phase 44.29 — Riga veicolo: tipo + marca/modello/colore */}
        <p className="text-xs text-dapnow-muted flex items-center gap-1 mt-0.5" data-testid="public-driver-vehicle-line">
          <VIcon className="h-3.5 w-3.5" />
          <span>{vehicleVisual || vm.label}</span>
        </p>
        {/* Targa su riga dedicata per visibilità al ritiro */}
        {driver.plate && (
          <p className="text-xs text-dapnow-ink mt-0.5" data-testid="public-driver-plate">
            Targa: <span className="font-mono font-semibold">{driver.plate}</span>
          </p>
        )}
      </div>
    </div>
  );
}

// Phase 44.12 — Prova di consegna premium con empty-state elegante + tap-to-fullscreen.
// La foto arriva come data URL pre-embedded dal backend (no auth/CORS issues).
// Se assente o invalida → empty-state pulito, NO placeholder browser rotto.
function DeliveryProofBlock({ photoDataUrl }) {
  const [fullscreen, setFullscreen] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const hasPhoto = Boolean(photoDataUrl) && !imgFailed;
  useBodyScrollLock(fullscreen);

  if (!hasPhoto) {
    return (
      <section
        className="rounded-2xl border border-dapnow-line bg-white p-5 sm:p-6 shadow-sm"
        data-testid="public-delivery-photo-empty"
      >
        <p className="overline inline-flex items-center gap-1.5 text-dapnow-muted">
          <PackageCheck className="h-3.5 w-3.5" /> Prova di consegna
        </p>
        <p className="mt-2 text-sm text-dapnow-muted leading-relaxed">
          La consegna è stata confermata dal trasportatore. Per questo ordine non è stata caricata una foto di prova.
        </p>
      </section>
    );
  }

  return (
    <>
      <section
        className="rounded-2xl border border-emerald-200 bg-white overflow-hidden shadow-sm"
        data-testid="public-delivery-photo"
      >
        <div className="px-4 sm:px-5 pt-4 pb-2 flex items-center justify-between">
          <p className="overline inline-flex items-center gap-1.5 text-emerald-700">
            <PackageCheck className="h-3.5 w-3.5" /> Prova di consegna
          </p>
          <span className="text-[10px] uppercase tracking-wider text-dapnow-muted font-bold">Tap per ingrandire</span>
        </div>
        <button
          type="button"
          onClick={() => setFullscreen(true)}
          className="block w-full focus:outline-none focus:ring-2 focus:ring-emerald-400 active:opacity-90 transition"
          data-testid="public-delivery-photo-thumb"
          aria-label="Ingrandisci la foto della consegna"
        >
          <img
            src={photoDataUrl}
            alt="Prova di consegna"
            onError={() => setImgFailed(true)}
            loading="lazy"
            className="w-full max-h-[420px] object-cover"
          />
        </button>
      </section>

      {fullscreen && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
          role="dialog"
          aria-modal="true"
          onClick={() => setFullscreen(false)}
          data-testid="public-delivery-photo-modal"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setFullscreen(false); }}
            className="absolute top-[calc(env(safe-area-inset-top)+1rem)] right-4 inline-flex items-center justify-center h-11 w-11 rounded-full bg-white/10 text-white backdrop-blur hover:bg-white/20 transition"
            aria-label="Chiudi"
            data-testid="public-delivery-photo-modal-close"
          >
            <XCircle className="h-6 w-6" />
          </button>
          <img
            src={photoDataUrl}
            alt="Prova di consegna (ingrandita)"
            className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}


function DeliveredReferralCard({ token, order }) {
  // Phase 44.11 — Esclusivo post-delivered. Non viene mai renderizzato prima
  // della consegna: il parent passa `isDelivered=true` come pre-condizione.
  // One-shot guard per il "viewed": il polling ricrea l'oggetto `order` ogni
  // 15s e non vogliamo flooderare l'evento. La chiave stabile è il token.
  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current || !token) return;
    viewedRef.current = true;
    trackPublicEvent("delivered_referral_viewed", {
      referrer_short_id: order?.order_short_id || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onCtaClick = (which) => {
    trackPublicEvent("delivered_referral_clicked", {
      cta: which,
      referrer_short_id: order?.order_short_id || null,
    });
  };

  // src=delivered_tracking → permette al Register.jsx di sparare l'evento
  // `delivered_referral_signup` post-signup distinto dal generico referral.
  const registerHref = `/register?ref=${encodeURIComponent(token || "")}&src=delivered_tracking`;

  return (
    <section
      className="rounded-2xl border border-dapnow-line bg-white shadow-sm overflow-hidden"
      data-testid="delivered-referral-card"
    >
      {/* Accent bar sottile in alto — verde DapNow per richiamare "consegnato" */}
      <div className="h-1 w-full bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-400" aria-hidden="true" />

      <div className="p-5 sm:p-6">
        <h3
          className="font-display text-xl sm:text-2xl font-extrabold tracking-tight text-dapnow-ink leading-tight"
          data-testid="delivered-referral-title"
        >
          Ti è piaciuta l&apos;esperienza DapNow?
        </h3>
        <p className="text-sm text-dapnow-muted mt-2 leading-relaxed">
          Spedisci anche tu con tracking live, notifiche e consegne rapide.
        </p>

        <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-2.5">
          <Link
            to={registerHref}
            onClick={() => onCtaClick("send_package")}
            data-testid="delivered-referral-cta-primary"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-dapnow-ink text-white h-12 px-5 text-sm font-bold tracking-tight hover:bg-dapnow-ink/90 active:scale-[0.98] transition w-full sm:w-auto"
          >
            <Send className="h-4 w-4" />
            <span>Spedisci un pacco</span>
          </Link>
          <Link
            to={registerHref}
            onClick={() => onCtaClick("get_credit")}
            data-testid="delivered-referral-cta-secondary"
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 h-12 px-5 text-sm font-bold hover:bg-emerald-100 active:scale-[0.98] transition w-full sm:w-auto"
          >
            <Sparkles className="h-4 w-4" />
            <span>Ricevi CHF 5 di credito</span>
          </Link>
        </div>

        <p className="mt-4 text-[11px] text-dapnow-muted leading-relaxed">
          Il credito viene accreditato automaticamente al tuo primo ordine completato.
        </p>
      </div>
    </section>
  );
}

export default function PublicTrackingPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/public/tracking/${token}`);
      setData(d);
      setError(null);
    } catch (e) {
      const code = e.response?.status;
      if (code === 410) setError({ kind: "expired", msg: "Questo link è scaduto o è stato revocato." });
      else if (code === 404) setError({ kind: "notfound", msg: "Link non valido." });
      else setError({ kind: "other", msg: "Impossibile caricare il tracking ora." });
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const trackingActive = data?.tracking_active;
    if (!trackingActive) return;
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load, data?.tracking_active]);

  const status = data?.status;
  const isDelivered = status === "delivered";
  const isCancelled = status === "cancelled";

  const hero = useMemo(() => {
    if (!status) return HERO_BY_STATUS.pending;
    const base = HERO_BY_STATUS[status] || HERO_BY_STATUS.pending;
    if (
      (status === "delivering" || status === "picked_up") &&
      data?.eta_target === "delivery" &&
      data?.eta_min !== null && data?.eta_min !== undefined &&
      data.eta_min <= NEAR_THRESHOLD_MIN
    ) {
      return NEAR_HERO;
    }
    return base;
  }, [status, data?.eta_target, data?.eta_min]);

  // Phase 44.11 — Memoize map props (stable refs) → evita re-fire dell'useEffect
  // interno di OrderTrackingMap su ogni render del polling (battery saver).
  // NB: gli hooks DEVONO stare prima di qualsiasi early-return JSX.
  const mapPickup = useMemo(
    () => (data?.pickup ? { lat: data.pickup.lat, lng: data.pickup.lng } : null),
    [data?.pickup?.lat, data?.pickup?.lng],
  );
  const mapDelivery = useMemo(
    () => (data?.delivery ? { lat: data.delivery.lat, lng: data.delivery.lng } : null),
    [data?.delivery?.lat, data?.delivery?.lng],
  );
  const mapDriver = useMemo(
    () => (data?.driver?.last_location
      ? {
          lat: data.driver.last_location.lat,
          lng: data.driver.last_location.lng,
          heading: data.driver.last_location.heading,
          speed_kmh: data.driver.last_location.speed_kmh,
        }
      : null),
    [
      data?.driver?.last_location?.lat,
      data?.driver?.last_location?.lng,
      data?.driver?.last_location?.heading,
      data?.driver?.last_location?.speed_kmh,
    ],
  );

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-4 bg-dapnow-bg">
        <Logo size="lg" />
        <div className="flex items-center gap-3 text-dapnow-muted text-sm">
          <Loader2 className="size-5 animate-spin text-dapnow-blue" /> Caricamento tracking…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-dapnow-bg flex items-center justify-center px-4" data-testid="public-tracking-error">
        <div className="max-w-md w-full rounded-2xl border border-dapnow-line bg-white p-8 text-center shadow-sm">
          <Logo size="lg" className="mb-6" />
          {error.kind === "expired"
            ? <XCircle className="size-10 mx-auto text-dapnow-red mb-3" />
            : <AlertCircle className="size-10 mx-auto text-amber-600 mb-3" />}
          <h1 className="font-display text-2xl font-extrabold tracking-tight">{error.msg}</h1>
          <p className="text-dapnow-muted text-sm mt-2">
            Se il pacco è ancora in viaggio, chiedi al mittente di rigenerare il link di tracking.
          </p>
          <Link to="/" className="mt-6 inline-block text-sm font-semibold text-dapnow-blue">
            ← Scopri DapNow.ch
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dapnow-bg overflow-x-hidden" data-testid="public-tracking-page">
      {/* HEADER MINIMALE */}
      <header className="bg-dapnow-ink text-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Logo variant="light" size="md" testId="public-tracking-logo" />
          <div className="shrink-0 text-right">
            <p className="text-[10px] uppercase tracking-wider text-white/55 font-bold">Tracking</p>
            <p className="text-xs font-semibold font-mono" data-testid="public-tracking-order-short">#{data.order_short_id}</p>
          </div>
        </div>
      </header>

      <main
        className="mx-auto max-w-3xl px-4 sm:px-6 py-5 space-y-4 pb-[calc(env(safe-area-inset-bottom)+1.5rem)]"
      >
        {/* HERO LIVE STATUS */}
        <section
          className={`relative overflow-hidden rounded-3xl border-2 ${hero.border} bg-gradient-to-br ${hero.bg} p-5 sm:p-7`}
          data-testid="public-tracking-hero"
          style={{ boxShadow: `0 6px 32px -10px ${hero.accent}55` }}
        >
          <div className="flex items-center gap-3">
            {hero.pulse && (
              <span className="relative inline-flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full rounded-full opacity-70 animate-ping" style={{ background: hero.accent }} />
                <span className="relative inline-flex rounded-full h-3 w-3" style={{ background: hero.accent }} />
              </span>
            )}
            <p className="overline text-[10px] font-bold" style={{ color: hero.accent }}>
              {hero.pulse ? "In tempo reale" : isDelivered ? "Consegna completata" : isCancelled ? "Annullato" : "Stato"}
            </p>
          </div>
          <h1
            className="mt-2 font-display text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight"
            style={{ color: hero.accent }}
            data-testid="public-tracking-hero-title"
          >
            {hero.title}
          </h1>
          <p className="mt-1.5 text-sm text-dapnow-ink/80">{hero.sub}</p>

          {/* Status mini-pill (live indicator) */}
          <div className="mt-4 flex items-center justify-between text-xs flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 text-dapnow-muted">
              {data.tracking_active ? (
                <>
                  <Wifi className="size-3" /> Aggiornamento live ogni {Math.round(POLL_MS/1000)}s
                </>
              ) : (
                <>
                  <WifiOff className="size-3" />
                  {isDelivered ? "Consegna completata" : isCancelled ? "Ordine annullato" : "In attesa"}
                </>
              )}
            </span>
            <span className="text-[10px] text-dapnow-muted">
              {data.matrix_provider === "google" ? "Routing Google" : data.matrix_provider === "haversine" ? "Routing approssimato" : ""}
            </span>
          </div>
        </section>

        {/* CTA Referral premium — Phase 44.11
            Renderizzato SOLO dopo "Consegnato": momento emotivamente migliore
            per la conversione (esperienza positiva fresca). Mai prima.
            Subito sotto la hero verde di consegna, senza essere invasivo. */}
        {isDelivered && (
          <DeliveredReferralCard token={token} order={data} />
        )}

        {/* MAPPA + ETA */}
        {(mapPickup && mapDelivery) && (
          <section className="rounded-2xl border border-dapnow-line bg-white overflow-hidden shadow-sm" data-testid="public-tracking-map-area">
            <OrderTrackingMap
              pickup={mapPickup}
              delivery={mapDelivery}
              driver={mapDriver}
              trail={null}
              className="h-60 sm:h-80 w-full"
            />
          </section>
        )}

        {!isCancelled && (
          <ETACard
            etaMin={data.eta_min}
            distanceKm={data.distance_to_target_km}
            lastLocAt={data.driver?.last_location_at}
            accent={hero.accent}
            isDelivered={isDelivered}
          />
        )}

        {/* DRIVER CARD */}
        <DriverCard driver={data.driver} accent={hero.accent} />

        {/* RITIRO / CONSEGNA — solo città/label corta */}
        <div className="grid sm:grid-cols-2 gap-3" data-testid="public-tracking-addresses">
          {data.pickup && (
            <div className="rounded-2xl border border-dapnow-line bg-white p-4">
              <p className="text-[10px] uppercase tracking-wider font-bold text-dapnow-muted flex items-center gap-1">
                <Package className="size-3 text-amber-600" /> Punto di ritiro
              </p>
              <p className="text-sm font-semibold mt-1 truncate">{data.pickup.label || "—"}</p>
            </div>
          )}
          {data.delivery && (
            <div className="rounded-2xl border border-dapnow-line bg-white p-4">
              <p className="text-[10px] uppercase tracking-wider font-bold text-dapnow-muted flex items-center gap-1">
                <Navigation className="size-3 text-dapnow-blue" /> Destinazione
              </p>
              <p className="text-sm font-semibold mt-1 truncate">{data.delivery.label || "—"}</p>
            </div>
          )}
        </div>

        {/* PROVA DI CONSEGNA — Phase 44.12: data URL embedded, fullscreen tap, empty-state premium */}
        {isDelivered && (
          <DeliveryProofBlock photoDataUrl={data.delivery_photo_data_url} />
        )}

        {/* Privacy disclaimer (compact) */}
        <p className="text-[11px] text-dapnow-muted text-center leading-relaxed px-2">
          Questo link mostra i punti di ritiro e consegna, lo stato e, quando disponibile, la posizione del driver. Condividilo solo con le persone coinvolte. Non mostra telefoni, email o dettagli di pagamento.
        </p>
      </main>
    </div>
  );
}
